import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon as Calendar, LocationIcon as MapPin, TimeIcon as Clock, ChevronRightIcon as ChevronRight, CloseIcon as X } from './Icons';

const getCategoryStyles = (category) => {
  switch (category) {
    case 'Gruppenstunde':
      return 'border-primary bg-primary/5 text-primary';
    case 'Lager':
      return 'border-tertiary bg-tertiary/5 text-tertiary';
    case 'Aktion':
      return 'border-secondary bg-secondary/5 text-secondary';
    default:
      return 'border-outline bg-surface-containerLowest text-surface-on';
  }
};

const formatDate = (dateString, showWeekday = true) => {
  const options = showWeekday
    ? { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' }
    : { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('de-DE', options);
};

const EventStack = ({ events, onClick }) => {
  const primaryEvent = events[0];
  const stackCount = events.length;
  const categoryStyles = getCategoryStyles(primaryEvent.category);

  return (
    <div
      onClick={onClick}
      className="relative w-full h-auto flex flex-col flex-1 cursor-pointer transition-transform duration-medium1 hover:-translate-y-1 group"
    >
      {/* Background Cards for Stack Effect */}
      {stackCount > 1 && (
        <>
          <div className="absolute -top-2 -right-2 w-full h-full rounded-2xl bg-surface-containerHighest/40 z-0 rotate-2"></div>
          <div className="absolute -top-1 -right-1 w-full h-full rounded-2xl bg-surface-containerHighest/60 z-[1] rotate-1"></div>
        </>
      )}

      {/* Main Card */}
      <div className={`relative z-[2] w-full flex-1 flex flex-col md3-card-elevated bg-surface-container rounded-2xl border border-outlineVariant/40 dark:border-white/15 border-l-[6px] p-6 shadow-elevation-1 transition-all duration-medium1 group-hover:bg-surface-containerHigh group-hover:shadow-elevation-2 ${categoryStyles.split(' ')[0]}`}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-label-small font-bold px-3 py-1 rounded-full border ${categoryStyles}`}>
              {primaryEvent.category}
            </span>
            {primaryEvent.linkedGroup && (
              <span
                className="text-label-small font-bold px-2.5 py-0.5 rounded-md truncate max-w-[140px]"
                style={{
                  backgroundColor: `${primaryEvent.linkedGroup.color || '#4CAF50'}20`,
                  color: primaryEvent.linkedGroup.color || '#4CAF50',
                  borderColor: `${primaryEvent.linkedGroup.color || '#4CAF50'}50`,
                  borderWidth: 1,
                  borderStyle: 'solid'
                }}
              >
                {primaryEvent.linkedGroup.name}
              </span>
            )}
          </div>
          {stackCount > 1 && (
            <div className="bg-error text-error-on w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm">
              {stackCount}
            </div>
          )}
        </div>

        <h3 className="text-title-large font-bold text-surface-on mb-2 line-clamp-1 leading-tight">
          {stackCount > 1 ? `${stackCount} Aktionen parallel` : primaryEvent.title}
        </h3>

        {stackCount > 1 ? (
          <ul className="text-body-small text-surface-onVariant/90 space-y-1.5 my-3 flex-grow">
            {events.slice(0, 3).map((e) => (
              <li key={e._id} className="font-medium flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                <span className="leading-snug">
                  {e.startTime ? `${e.startTime} Uhr: ` : ''}
                  <strong className="text-surface-on font-semibold">{e.title}</strong>
                </span>
              </li>
            ))}
            {stackCount > 3 && (
              <li className="text-[11px] text-surface-onVariant/60 italic pl-3">
                + {stackCount - 3} weitere Aktion(en)...
              </li>
            )}
          </ul>
        ) : (
          <h4 className="text-body-medium text-surface-onVariant mb-4 line-clamp-2 min-h-[3rem] leading-relaxed">
            {primaryEvent.description || 'Keine Beschreibung vorhanden.'}
          </h4>
        )}

        <div className="flex flex-col gap-2 mt-auto">
          <div className="flex items-center gap-2 text-surface-onVariant">
            <Calendar sx={{ fontSize: 16 }} />
            <span className="text-body-small">{formatDate(primaryEvent.date)}</span>
          </div>
          {stackCount === 1 ? (
            <>
              {(primaryEvent.startTime || primaryEvent.endTime) && (
                <div className="flex items-center gap-2 text-surface-onVariant">
                  <Clock sx={{ fontSize: 16 }} />
                  <span className="text-body-small">
                    {primaryEvent.startTime} {primaryEvent.endTime ? `- ${primaryEvent.endTime}` : ''} Uhr
                  </span>
                </div>
              )}
              {primaryEvent.location && (
                <div className="flex items-center gap-2 text-surface-onVariant overflow-hidden">
                  <MapPin sx={{ fontSize: 16 }} />
                  <span className="text-body-small truncate">{primaryEvent.location}</span>
                </div>
              )}
            </>
          ) : (
            <div className="mt-2 text-label-small text-surface-onVariant/60 italic">
              Klicke für Details & Uhrzeiten
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-outlineVariant">
          <span className="text-label-small uppercase font-bold text-primary">
            {stackCount > 1 ? 'Details anzeigen' : 'Mehr erfahren'}
          </span>
        </div>
      </div>
    </div>
  );
};

const EventStackModal = ({ events, onClose }) => {
  // Close on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div
        className="absolute inset-0 bg-surface-scrim/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />
      <div className="relative w-full max-w-4xl bg-surface-containerLow rounded-[1.5rem] sm:rounded-[2rem] shadow-elevation-5 overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="p-6 sm:p-8 pb-4 flex justify-between items-start border-b border-outlineVariant">
          <div className="pr-4">
            <h2 className="text-title-large sm:text-headline-medium font-bold text-surface-on leading-tight">
              Aktionen am {formatDate(events[0].date)}
            </h2>
            <p className="text-body-small sm:text-body-medium text-surface-onVariant mt-1">
              {events.length} parallele Termine an diesem Tag
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full hover:bg-surface-containerHigh flex items-center justify-center transition-colors shrink-0"
          >
            <X sx={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-8 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {events.map((event) => {
              const catStyles = getCategoryStyles(event.category);
              return (
                <div key={event._id} className="md3-card-elevated bg-surface-container p-5 sm:p-6 rounded-2xl border-l-4 shadow-elevation-1 flex flex-col h-full transition-colors hover:bg-surface-containerHigh">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className={`inline-block text-label-small font-bold px-3 py-1 rounded-full border ${catStyles}`}>
                      {event.category}
                    </span>
                    {event.linkedGroup && (
                      <span
                        className="text-label-small font-bold px-2.5 py-0.5 rounded-md truncate max-w-[160px]"
                        style={{
                          backgroundColor: `${event.linkedGroup.color || '#4CAF50'}20`,
                          color: event.linkedGroup.color || '#4CAF50',
                          borderColor: `${event.linkedGroup.color || '#4CAF50'}50`,
                          borderWidth: 1,
                          borderStyle: 'solid'
                        }}
                      >
                        {event.linkedGroup.name}
                      </span>
                    )}
                  </div>

                  <h3 className="text-title-medium sm:text-title-large font-bold text-surface-on mb-4 leading-snug">
                    {event.title}
                  </h3>

                  <div className="flex flex-col gap-2 mb-4 text-surface-onVariant">
                    {(event.startTime || event.endTime) && (
                      <div className="flex items-center gap-2">
                        <Clock sx={{ fontSize: 16 }} />
                        <span className="text-body-small font-semibold">
                          {event.startTime} {event.endTime ? `- ${event.endTime}` : ''} Uhr
                        </span>
                      </div>
                    )}
                    {event.location && (
                      <div className="flex items-center gap-2">
                        <MapPin sx={{ fontSize: 16 }} />
                        <span className="text-body-small">{event.location}</span>
                      </div>
                    )}
                  </div>

                  {event.description && (
                    <p className="text-body-medium text-surface-onVariant whitespace-pre-line mb-6 line-clamp-4 leading-relaxed">
                      {event.description}
                    </p>
                  )}

                  <div className="mt-auto pt-4 flex justify-end">
                    {event.slug ? (
                      <Link
                        to={`/events/${event.slug}`}
                        onClick={onClose}
                        className="md3-btn-text text-primary flex items-center gap-2 font-bold"
                      >
                        Details <ChevronRight sx={{ fontSize: 18 }} />
                      </Link>
                    ) : event.link ? (
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="md3-btn-text text-primary flex items-center gap-2 font-bold"
                      >
                        Mehr Infos <ChevronRight sx={{ fontSize: 18 }} />
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export { EventStack, EventStackModal };
export default EventStack;
